import React, { useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import DashboardSidebar from "../../components/DashboardSidebar";
import useFetch from "../../hooks/useFetch";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareStoreDialog from "./ShareStoreDialog";
import "./ProductGroups.css";
import TextInput from "../ui/Input/TextInput";
import { useCurrentStore } from "../../hooks/useCurrentStore";


const CollectionToggleBtn: React.FC<{
  colId: number;
  isActive: boolean;
  onDone: () => void;
  onError: (content: React.ReactNode) => void;
}> = ({ colId, isActive, onDone, onError }) => {

  const { fetchData: toggleStatus } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/productgroups/togglestatus/${colId}`,
    method: "PUT",
  });

  const handleClick = async () => {
    const action = isActive ? "deactivate" : "activate";
    const confirmed = window.confirm(`Are you sure you want to ${action} this Product Group?`);
    if (!confirmed) return;
    const result = await toggleStatus({ status: isActive ? 0 : 1 });
    if (result?.success) {
      onDone();
    } else {
      const msg = result?.code === 401 || result?.message?.toLowerCase().includes("token")
        ? <>⚠️ Session expired. Please <a href="/login" style={{ color: "#a59cff", textDecoration: "underline" }}>login</a> again.</>
        : (result?.message || `Failed to ${action}.`);
      onError(msg);
    }
  };

  return (
    <span
      className={`collections-action-link ${isActive ? "collections-action-link--danger" : "collections-action-link--activate"}`}
      onClick={handleClick}
    >
      {isActive ? "Deactivate" : "Activate"}
    </span>
  );
};

const Collections: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const navigate = useNavigate();
  const userStoreId = useCurrentStore(store_id);
  const { data, fetchData, isLoading, errorContent, hasFetched } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/productgroups/${userStoreId}`,
    method: "GET",
  });

  useEffect(() => {
    if (userStoreId) fetchData();
  }, [userStoreId, fetchData]); 

useEffect(() => {
  navigate(`/product-groups/${userStoreId}`, { replace: true });
}, [userStoreId]);

const [collections, setCollections] = React.useState<any[]>([]);
const [shareOpen, setShareOpen] = React.useState(false);
const [toggleError, setToggleError] = React.useState<React.ReactNode>(null);

const [formTitle, setFormTitle] = React.useState("");
const [formDesc, setFormDesc] = React.useState("");
const [formMsg, setFormMsg] = React.useState("");
const [editingColId, setEditingColId] = React.useState<number | null>(null);
const [isSubmitting, setIsSubmitting] = React.useState(false);


  const { fetchData: createCollection } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/productgroups/add`,
    method: "POST",
  });

  const { fetchData: updateCollection } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/productgroups/update/${editingColId}`,
    method: "PUT",
  });

React.useEffect(() => {
if (data?.storeProductGroups) setCollections(data.storeProductGroups);
}, [data]);

const currentRole = JSON.parse(localStorage.getItem("user") || '{}').current_role;
const location = useLocation();
const storeName = location.state?.storeName?.split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') ?? "";
const storeUrl = `${import.meta.env.VITE_STORE_BASE_URL}${userStoreId}`;

const toTitleCase = (str: string) =>
str?.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

// ADD THIS
const handleFormSubmit = async () => {
  if (!formTitle.trim()) { setFormMsg("Title is required."); return; }
  setIsSubmitting(true);
  setFormMsg("");

  const wasEditing = editingColId;

  const result = editingColId
    ? await updateCollection({ store_id: Number(userStoreId), title: formTitle, description: formDesc || "" })
    : await createCollection({ store_id: Number(userStoreId), title: formTitle, description: formDesc || "", status: "1" });

  setIsSubmitting(false);

  if (result?.success) {
    if (wasEditing) {
      setCollections(prev =>
        prev.map(c => c.id === wasEditing
          ? { ...c, title: formTitle, description: formDesc || "" }
          : c
        )
      );
    } else {
      const newGroup = result?.data?.storeProductGroup;
      if (newGroup) {
        setCollections(prev => [{ ...newGroup, status: Number(newGroup.status), product_count: 0 }, ...prev]);
      }
    }
    setFormTitle("");
    setFormDesc("");
    setEditingColId(null);
    setFormMsg(wasEditing ? "Updated successfully!" : "Added successfully!");
    setTimeout(() => setFormMsg(""), 2500);
  } else {
    setFormMsg(result?.message || "Something went wrong.");
  }
};


  return (
    <>
    <div className="collections-page-wrapper">
      {currentRole !== 1 && <DashboardSidebar />}
          <div className="collections-content">
            <div className="div-block-28">
        <div className="collections-toolbar">
          {(currentRole==1)?(
            <span onClick={() => window.history.back()} className="collections-back-btn">
              <ArrowBackIcon />
              {storeName && <span className="collections-back-label">{storeName}</span>}
            </span>
          ) : <span />}
            <span className="collections-section-heading">Add / Edit Product Groups</span>
            <div className="collections-inline-form">
              <form className="collections-inline-form-row" onSubmit={(e) => { e.preventDefault(); handleFormSubmit(); }}>
                  <div className="collections-name-wrap">
                    <TextInput type="text" label="Group Name" placeholder="Group Name *" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} />
                  </div>
                <div className="collections-desc-wrap">
                  <TextInput type="text" label="Description" placeholder="Description" value={formDesc} onChange={(e) => setFormDesc(e.target.value)} />
                </div>
                <button type="submit" className="collection-button w-button" disabled={isSubmitting}>
                  Submit
                </button>
              </form>
              <span className="collections-inline-msg">{formMsg}</span>
            </div>
            </div>
            <div className="collections-table-wrapper">

              {/* Header Row */}
              <div className="collections-row collections-row--header">
                <div className="collections-cell collections-cell--title   collections-cell--header">Title</div>
                <div className="collections-cell collections-cell--desc    collections-cell--header">Description</div>
                <div className="collections-cell collections-cell--count   collections-cell--header">Products</div>
                <div className="collections-cell collections-cell--actions   collections-cell--header">Actions</div>
              </div>

              {/* States */}
              {(!hasFetched || isLoading) && (
                <span className="collections-status-text">Loading Product Groups...</span>
              )}
              {errorContent && (
                <div className="collections-status-text">{errorContent}</div>
              )}
              {toggleError && (
                <div className="collections-status-text">{toggleError}</div>
              )}
              {hasFetched && !isLoading && collections.length === 0 && !(errorContent) && (
                <span className="collections-status-text">No Product Groups available yet.</span>
              )}

              {/* Data Rows */}
              {hasFetched && !isLoading && collections.map((col: any) => (
                <div className="collections-row collections-row--data" key={col.id}>

                  <div className="collections-cell collections-cell--title" data-label="Title">
                    <span className="collections-cell-text">{toTitleCase(col.title) ?? "Placeholder Title"}</span>
                    <span className="collections-tip">{toTitleCase(col.title) ?? "Placeholder Title"}</span>
                  </div>

                  <div className="collections-cell collections-cell--desc" data-label="Description">
                    <span className="collections-cell-text">{toTitleCase(col.description) ?? "—"}</span>
                    <span className="collections-tip">{toTitleCase(col.description) ?? "—"}</span>
                  </div>

                  <div className="collections-cell collections-cell--count" data-label="Products">
                    <span className="collections-cell-text">{col.product_count ?? 0}</span>
                  </div>

                  <div className="collections-cell collections-cell--actions" data-label="Actions">
                    <div className="collections-actions">
                      <Link 
                        to={`/product-groups/edit/${col.id}`} 
                        state={{ collection: col, tab: "products", storeName: storeName, storeId: userStoreId }} 
                        className="collections-action-link"
                      >Products
                      </Link>
                      <span
                        className="collections-action-link"
                        onClick={() => { setEditingColId(col.id); setFormTitle(col.title); setFormDesc(col.description ?? ""); setFormMsg(""); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      >Edit
                      </span>
                      <CollectionToggleBtn
                        colId={col.id}
                        isActive={col.status === 1}
                        onDone={() =>
                          setCollections(prev =>
                            prev.map(c => c.id === col.id ? { ...c, status: col.status === 1 ? 0 : 1 } : c)
                          )
                        }
                        onError={(content) => setToggleError(content)}
                      />
                    </div>
                  </div>

                </div>
              ))}

            </div>
          </div>
        </div>
        </div>
        <ShareStoreDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          storeUrl={storeUrl}
          storeName={storeName}
        />
    </>
  );
};

export default Collections;
